import React, { useState, useCallback, useEffect } from "react";

// recoil
import { useRecoilValue, useSetRecoilState } from "recoil";
import { userCredentials, routeInfo } from "recoilState/GlobalState";
import PersonIcon from "@mui/icons-material/Person";
// utils
import { useFormik } from "formik";
import { Buttons, HeaderButton } from "components";
import {
    FilterAltOffRounded,
    EditOutlined,
    DeleteOutline,
    Add,
    Cached,
    VisibilityOutlined,
} from "@mui/icons-material";
import Filters from "components/Filters";
import { hoteles as titles } from "consts/titulos-de-tablas";
import { hotelGet } from "services/services-mia/SFHotel";
import { provinciaGet } from "services/services-mia/miakey";

// styles
import s from "styles/pages/Pages.module.scss";
import { ACTIONS } from "consts/Actions";
import { Routes } from "consts/Routes-App";
import Modal from "components/UI/Modal/Modal";
import {
    InsertModal,
    UpdateModal,
    DeleteModal,
    DetailModal,
    UpdateStateModal,
} from "./Actions";
import XGrid from "components/UI/Grid/XGrid";
import { useService } from "components/route/ServiceRoute";
import { snackbarData } from "recoilState/GlobalState";
import { ChangeState } from "assets/icons";

// Mock de estados
const ESTADOS_HOTEL = [
    { id: "", label: "Todos" },
    { id: "A", label: "Activo" },
    { id: "B", label: "Baja" },
    { id: "S", label: "Suspendido" },
];

const formatPhone = (phone) => {
    if (!phone) return "";

    //Lo convertimos a string puro para matar los puntos de los miles
    const cleanPhone = String(phone).trim().replace(/\./g, "");
    //Si tiene 10 dígitos (típico celular/fijo argentino con característica)
    if (cleanPhone.length === 10) {
        const codigoArea = cleanPhone.slice(0, 2);   // "11"
        const primeraParte = cleanPhone.slice(2, 6); // "2458"
        const segundaParte = cleanPhone.slice(6);    // "7584"

        return `${codigoArea} ${primeraParte}-${segundaParte}`;
    }
    //Si viene con otra longitud (un 0341, un internacional, etc.), al menos lo devolvemos como texto limpio
    return cleanPhone;
}

export default function Hoteles() {
    const [modalDetails, setModalDetails] = useState({ isOpen: false });
    const [isLoading, setIsLoading] = useState(false);
    const [rowSelected, setRowSelected] = useState({ status: false, row: {} });
    const [rows, setRows] = useState([]);
    const [provincias, setProvincias] = useState([]);
    const credentials = useRecoilValue(userCredentials);
    const setSnackbarInfo = useSetRecoilState(snackbarData);
    const setRoute = useSetRecoilState(routeInfo);

    const { service } = useService();

    useEffect(() => {
        setRoute(Routes.hoteles);
    }, [setRoute]);

    const updateList = useCallback(
        async (formikValues) => {
            setRowSelected({ status: false, row: {} });
            setIsLoading(true);
            setRows([]);
            const { nombre, estado } = formikValues || {};
            try {
                const { result } = await hotelGet(
                    { SFHotelNombre: nombre, SFHotelStatus: estado },
                    credentials
                );

                setRows(
                    result.map((item) => {
                        const {
                            SFHotelId,
                            SFHotelCUIT,
                            SFHotelCheckin,
                            SFHotelCheckout,
                            SFHotelDomicilioCPA,
                            SFHotelDomicilioCalle,
                            SFHotelDomicilioCasa,
                            SFHotelNombre,
                            SFHotelEmail,
                            SFHotelTelefono,
                            SFHotelStatus,
                            SFHotelLatitud,
                            SFHotelLongitud,
                            provinciaNombre,
                            provinciaId,
                            localidadId,
                            localidadNombre,
                            SFHotelVersion,
                        } = item;

                        return {
                            id: crypto.randomUUID(),
                            SFHotelId,
                            SFHotelVersion,
                            SFHotelCUIT,
                            SFHotelCheckin,
                            SFHotelCheckout,
                            SFHotelDomicilioCPA,
                            SFHotelDomicilioCalle,
                            SFHotelDomicilioCasa,
                            SFHotelNombre,
                            SFHotelEmail,
                            SFHotelTelefono,
                            SFHotelStatus,
                            SFHotelLatitud,
                            SFHotelLongitud,
                            provinciaId,
                            localidadId,
                            localidadNombre,
                            [titles[0].field]: SFHotelNombre,
                            [titles[1].field]: SFHotelCUIT,
                            [titles[2].field]: provinciaNombre,
                            [titles[3].field]: localidadNombre,
                            [titles[4].field]:
                                SFHotelStatus === "A"
                                    ? "Activo"
                                    : SFHotelStatus === "B"
                                        ? "Baja"
                                        : "Suspendido",
                            [titles[5].field]: formatPhone(SFHotelTelefono),
                            [titles[6].field]: SFHotelEmail,
                            [titles[7].field]: SFHotelCheckin?.slice(0, 5),
                            [titles[8].field]: SFHotelCheckout?.slice(
                                0,
                                5
                            ),
                        };
                    })
                );
            } catch (error) {
                setSnackbarInfo({
                    open: true,
                    severity: "error",
                    message: error?.message || "Error al obtener los hoteles",
                });
            } finally {
                setIsLoading(false);
            }
        },
        [credentials, setSnackbarInfo]
    );

    const formik = useFormik({
        initialValues: {
            nombre: "",
            estado: "",
        },
        onSubmit: () => {
            updateList(formik.values);
        },
    });

    const resetFilters = () => {
        formik.resetForm();
        updateList();
    };

    const handleClick = ({ procede, title }) => {
        setModalDetails({ title, procede, isOpen: true });
    };

    const closeModal = () => {
        setModalDetails({ isOpen: false });
    };

    useEffect(() => {
        updateList();
    }, [updateList]);

    useEffect(() => {
        if (Object.keys(credentials).length > 0 && service?.servicioCod) {
            (async () => {
                const { result: resultProvincias } = await provinciaGet(
                    {},
                    credentials
                );

                setProvincias(
                    resultProvincias.filter((r) => r.provinciaSantaFeId)
                );
            })();
        }
    }, [credentials, service]);

    return (
        <main className={s.main}>
            <Modal
                onClose={closeModal}
                open={modalDetails.isOpen}
                title={modalDetails.title}
            >
                {modalDetails.procede === ACTIONS.INSERT && (
                    <InsertModal
                        updateList={updateList}
                        closeModal={closeModal}
                        provincias={provincias}
                    />
                )}
                {modalDetails.procede === ACTIONS.UPDATE && (
                    <UpdateModal
                        updateList={updateList}
                        closeModal={closeModal}
                        provincias={provincias}
                        hotel={rowSelected.row}
                        isViewDetail={false}
                    />
                )}
                {modalDetails.procede === ACTIONS.BATCH && (
                    <UpdateStateModal
                        updateList={updateList}
                        closeModal={closeModal}
                        hotel={rowSelected.row}
                    />
                )}
                {modalDetails.procede === ACTIONS.DELETE && (
                    <DeleteModal
                        updateList={updateList}
                        closeModal={closeModal}
                        hotel={rowSelected.row}
                    />
                )}
                {modalDetails.procede === ACTIONS.DETAIL && (
                    <DetailModal
                        hotel={rowSelected?.row}
                        closeModal={closeModal}
                    />
                )}
                {modalDetails.procede === ACTIONS.VIEW && (
                    <UpdateModal
                        updateList={updateList}
                        closeModal={closeModal}
                        provincias={provincias}
                        hotel={rowSelected?.row}
                        isViewDetail={true}
                    />
                )}
            </Modal>
            <div className={s["filters-actions"]}>
                <div style={{ display: "flex", alignItems: "center" }}>
                    <Filters
                        formik={formik}
                        inputsRender={[
                            {
                                type: "text",
                                name: "nombre",
                                label: "Nombre",
                                disabled: false,
                            },
                            {
                                type: "select",
                                name: "estado",
                                label: "Estado",
                                disabled: false,
                                options: ESTADOS_HOTEL,
                                firstOptionKey: "id",
                                firstOptionName: "label",
                                firstOptionValue: "id",
                            },
                        ]}
                    />
                    <HeaderButton
                        text={"Limpiar Filtros"}
                        onClick={resetFilters}
                    >
                        <FilterAltOffRounded />
                    </HeaderButton>
                </div>
                <Buttons>
                    <HeaderButton text={"Actualizar"} onClick={updateList}>
                        <Cached />
                    </HeaderButton>
                    <HeaderButton
                        text={"Agregar Hotel"}
                        handleClick={handleClick}
                        data={{
                            title: "Agregar Hotel",
                            procede: ACTIONS.INSERT,
                        }}
                        disabled={rowSelected.row.id}
                    >
                        <Add />
                    </HeaderButton>
                    <HeaderButton
                        text={"Editar Hotel"}
                        disabled={
                            !rowSelected.row.id ||
                            rowSelected.row.SFHotelStatus === "B"
                        }
                        handleClick={handleClick}
                        data={{
                            title: "Editar Hotel",
                            procede: ACTIONS.UPDATE,
                        }}
                    >
                        <EditOutlined />
                    </HeaderButton>

                    <HeaderButton
                        text={"Ver personal"}
                        handleClick={handleClick}
                        data={{
                            title: `Personal de ${rowSelected.row?.SFHotelNombre ?? ""
                                }`,
                            procede: ACTIONS.DETAIL,
                        }}
                        disabled={
                            !rowSelected.row.id ||
                            rowSelected.row.SFHotelStatus === "B"
                        }
                    >
                        <PersonIcon />
                    </HeaderButton>

                    <HeaderButton
                        text={"Detalles del Hotel"}
                        handleClick={handleClick}
                        data={{
                            title: `Detalles del Hotel`,
                            procede: ACTIONS.VIEW,
                        }}
                        disabled={!rowSelected.row.id}
                    >
                        <VisibilityOutlined />
                    </HeaderButton>

                    <HeaderButton
                        text={"Cambiar estado"}
                        disabled={
                            !rowSelected.row.id ||
                            rowSelected.row.SFHotelStatus === "B"
                        }
                        handleClick={handleClick}
                        data={{
                            title: "Cambiar estado",
                            procede: ACTIONS.BATCH,
                        }}
                        icon={ChangeState}

                    >

                    </HeaderButton>

                    <HeaderButton
                        text={"Eliminar Hotel"}
                        disabled={!rowSelected.row.id}
                        handleClick={handleClick}
                        data={{
                            title: "eliminar",
                            procede: ACTIONS.DELETE,
                        }}

                    >
                        <DeleteOutline />
                    </HeaderButton>
                </Buttons>
            </div>
            <div className={s.container}>
                <XGrid
                    rows={rows}
                    titles={titles}
                    loading={isLoading}
                    secondarySelected={rowSelected}
                    setSecondarySelected={setRowSelected}
                />
            </div>
        </main>
    );
}
